import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import apiRequest from '../../api/request';
import EffectiveFromField from './EffectiveFromField';
import { emptyEffective } from './effectiveWindow';
import { validateSlab } from './thresholdValidation';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentIdByName = (name) => DEPARTMENTS.find((d) => d.name.toLowerCase() === String(name).trim().toLowerCase())?.id;

// CSV download -> edit -> re-upload, same pattern as views/targetManagement/components/
// BulkTargetUpload.jsx (and views/finance/FeeForecastLatest/BulkUpload.jsx before it) - the
// established "edit N rows at once" idiom in this app. Built because the regular Add/Edit panel
// only lets Finance save one slab at a time, closing and reopening for every single range or
// department - genuinely bad when setting up several ranges for one department, or copying the
// same slab across several departments (just duplicate the row in Excel and change the
// Department cell). The CSV includes every CURRENT slab (not just a blank template) so Finance
// can tweak existing ones and append new rows in the same file, then upload once.
const downloadCSV = (rows) => {
  const headers = ['id', 'department', 'target_min', 'target_max', 'tar_threshold', 'arpu_threshold', 'admission_count_threshold', 'lcrr_threshold', 'is_active'];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) => [
      r.id,
      `"${DEPARTMENTS.find((d) => d.id === Number(r.department_id))?.name || r.department_id}"`,
      r.target_min,
      r.target_max ?? '',
      r.tar_threshold,
      r.arpu_threshold,
      r.admission_count_threshold,
      r.lcrr_threshold,
      r.is_active ? 'TRUE' : 'FALSE',
    ].join(',')),
    // A few blank rows at the end, ready for new slabs - leave `id` blank on a new row, pick a
    // Department name from the list above (case-insensitive), leave target_max blank for "and above".
    ...Array.from({ length: 5 }, () => `,,,,,,,,`),
  ];
  // Leading BOM so Excel on Windows reads this as UTF-8 instead of garbling anything non-ASCII.
  const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'target_slabs_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Quote-aware split - a plain `line.split(',')` breaks the moment any cell contains a comma.
const splitCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '').map(splitCsvLine);
  const headers = lines[0].map((h) => h.replace(/"/g, '').trim());
  return lines
    .slice(1)
    .map((row) => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i]?.trim() ?? ''; });
      return obj;
    })
    // A blank template row (every cell empty) isn't a row to save - drop it rather than erroring.
    .filter((r) => r.department || r.target_min || r.target_max || r.tar_threshold || r.arpu_threshold || r.admission_count_threshold || r.lcrr_threshold);
};

export default function TargetSlabBulkUpload({ rows, disabled, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [effective, setEffective] = useState(emptyEffective);

  const handleUpload = () => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (!parsed.length) {
        setError('No rows found in that file.');
        return;
      }

      const badRows = [];
      const entries = parsed.map((r, idx) => {
        const departmentId = departmentIdByName(r.department);
        if (!departmentId) {
          badRows.push(`Row ${idx + 2}: "${r.department}" isn't a recognized department (${DEPARTMENTS.map((d) => d.name).join(', ')})`);
          return null;
        }
        const entry = {
          id: r.id ? Number(r.id) : undefined,
          department_id: departmentId,
          target_min: r.target_min,
          target_max: r.target_max,
          tar_threshold: r.tar_threshold,
          arpu_threshold: r.arpu_threshold,
          admission_count_threshold: r.admission_count_threshold,
          lcrr_threshold: r.lcrr_threshold,
          is_active: /^(true|yes|1)$/i.test(r.is_active) || r.is_active === '',
        };
        // BUG-03: an empty cell used to be saved as 0, and negatives / TAR or LCRR over 100% went in.
        // Say which row and which cell, before anything is sent (the server checks the same rules).
        Object.values(validateSlab(entry)).forEach((message) => badRows.push(`Row ${idx + 2}: ${message}`));
        return entry;
      });

      if (badRows.length) {
        const shown = badRows.slice(0, 8).join('; ');
        setError(badRows.length > 8 ? `${shown}; and ${badRows.length - 8} more problem(s)` : shown);
        return;
      }

      setUploading(true);
      apiRequest({ url: 'warning-dashboard/target-slabs/bulk', method: 'post', data: { entries, effective_month: effective.effective_month, effective_until: effective.effective_until, only_this_month: effective.only_this_month } })
        .then((res) => {
          if (res?.status !== 'success') {
            setError(res?.message || 'Could not save these slabs');
            return;
          }
          enqueueSnackbar(res.message || `${entries.length} slab(s) saved`, { variant: 'success' });
          setOpen(false);
          setFile(null);
          onSaved?.();
        })
        .catch((err) => setError(err?.response?.data?.message || 'Could not save these slabs'))
        .finally(() => setUploading(false));
    };
    reader.readAsText(file);
  };

  return (
    <GlobalDialog
      title="Bulk Add / Edit Target Slabs"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      closeCondition={() => setOpen(false)}
      popupButton={
        <Button size="small" variant="contained" disabled={disabled} onClick={() => { setEffective(emptyEffective()); setOpen(true); }}>
          Bulk Upload
        </Button>
      }
      content={
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Download the CSV, edit it in Excel, then upload it back - add as many new rows as you need (leave{' '}
            <b>id</b> blank for a new slab), or edit an existing row in place to update it. Department must match
            one of: {DEPARTMENTS.map((d) => d.name).join(', ')}. Leave <b>target_max</b> blank for "and above".
          </Typography>

          <EffectiveFromField form={effective} setForm={setEffective} />

          <Button
            startIcon={<Icon icon="line-md:download-loop" width={22} height={22} />}
            variant="outlined"
            onClick={() => downloadCSV(rows.filter((r) => !r.effective_to)) /* the open-ended slabs - a one-month override is not a row to edit in a sheet */}
          >
            Download CSV
          </Button>

          <Button startIcon={<Icon icon="material-symbols:upload" width={22} height={22} />} variant="outlined" component="label">
            Select CSV File
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={(e) => { setFile(e.target.files[0]); setError(null); }}
            />
          </Button>

          {file && (
            <Typography variant="body2">
              Selected file: <b>{file.name}</b>
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || !file}
              startIcon={uploading ? <CircularProgress size={18} /> : null}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      }
    />
  );
}
